import { FormEvent, useCallback, useEffect, useState } from 'react';

import { ErroRespostaHttp } from '../../../shared/api/http';
import { listarJogadores, listarTurmas } from '../../jogadores/api/jogadores';
import type { Jogador, TurmaResumo } from '../../jogadores/api/jogadores';
import {
    alterarParticipante, criarGrupo, criarParticipante, criarTurma, detalharTurma, trocarCodigo,
} from '../api/cadastro';
import type { AlteracaoParticipante, GrupoCadastrado } from '../api/cadastro';
import { manterSeDisponivel, useTurmaEmUso } from '../../../shared/turmaEmUso';

interface CadastroPainelProps {
    csrfToken: string;
}

/** O código aparece uma única vez: fica na tela até o operador dispensar ou gerar outro. */
interface CodigoEmitido {
    matricula: string;
    nome: string | null;
    codigo: string;
    sessoesEncerradas: number;
}

const SITUACAO = { ativo: 'Ativo', inativo: 'Inativo', bloqueado: 'Bloqueado' } as const;

/** A mensagem do servidor já é para o operador; sem ela, uma genérica. */
function mensagemDoErro(erro: unknown): string {
    if (erro instanceof ErroRespostaHttp) {
        if (typeof erro.corpo?.mensagem === 'string') return erro.corpo.mensagem;
        if (erro.status === 422) return 'Confira os campos: há valor em branco ou fora do formato.';
        if (erro.status === 401 || erro.status === 403) return 'Sessão expirada ou sem permissão. Entre de novo.';
    }
    return 'Não foi possível concluir. Tente novamente.';
}

export function CadastroPainel({ csrfToken }: CadastroPainelProps) {
    const [turmas, setTurmas] = useState<TurmaResumo[] | null>(null);
    const [turmaId, setTurmaId] = useTurmaEmUso();
    const [grupos, setGrupos] = useState<GrupoCadastrado[]>([]);
    const [jogadores, setJogadores] = useState<Jogador[]>([]);
    const [erro, setErro] = useState<string | null>(null);
    const [ocupado, setOcupado] = useState(false);
    const [codigo, setCodigo] = useState<CodigoEmitido | null>(null);

    const [novaTurma, setNovaTurma] = useState({ id: '', nome: '' });
    const [novoGrupo, setNovoGrupo] = useState({ id: '', nome: '' });
    const [novo, setNovo] = useState({ matricula: '', nome: '', grupo_id: '', lider: false });

    const carregarTurmas = useCallback(async (selecionar?: string) => {
        try {
            const dados = await listarTurmas();
            setTurmas(dados);
            setTurmaId((atual) => selecionar ?? manterSeDisponivel(atual, dados.map((turma) => turma.id), dados[0]?.id ?? null));
        } catch (falha) {
            setTurmas([]);
            setErro(mensagemDoErro(falha));
        }
    }, []);

    const carregarTurma = useCallback(async (alvo: string) => {
        try {
            const [detalhe, lista] = await Promise.all([detalharTurma(alvo), listarJogadores(alvo)]);
            setGrupos(detalhe.grupos);
            setJogadores(lista.jogadores);
            setNovo((atual) => ({
                ...atual,
                grupo_id: detalhe.grupos.some((g) => g.id === atual.grupo_id) ? atual.grupo_id : detalhe.grupos[0]?.id ?? '',
            }));
        } catch (falha) {
            setErro(mensagemDoErro(falha));
        }
    }, []);

    useEffect(() => {
        void carregarTurmas();
    }, [carregarTurmas]);

    useEffect(() => {
        setCodigo(null);
        if (turmaId) void carregarTurma(turmaId);
    }, [turmaId, carregarTurma]);

    /** Toda escrita passa por aqui: trava os botões, mostra a recusa e relê a turma. */
    async function executar(acao: () => Promise<void>) {
        setErro(null);
        setOcupado(true);
        try {
            await acao();
        } catch (falha) {
            setErro(mensagemDoErro(falha));
        } finally {
            setOcupado(false);
        }
    }

    function aoCriarTurma(evento: FormEvent) {
        evento.preventDefault();
        void executar(async () => {
            const criada = await criarTurma({ id: novaTurma.id.trim(), nome: novaTurma.nome.trim() }, csrfToken);
            setNovaTurma({ id: '', nome: '' });
            await carregarTurmas(criada.id);
        });
    }

    function aoCriarGrupo(evento: FormEvent) {
        evento.preventDefault();
        if (!turmaId) return;
        void executar(async () => {
            await criarGrupo(turmaId, { id: novoGrupo.id.trim(), nome: novoGrupo.nome.trim() }, csrfToken);
            setNovoGrupo({ id: '', nome: '' });
            await carregarTurma(turmaId);
        });
    }

    function aoCadastrar(evento: FormEvent) {
        evento.preventDefault();
        if (!turmaId) return;
        void executar(async () => {
            const criado = await criarParticipante(
                turmaId, { ...novo, matricula: novo.matricula.trim(), nome: novo.nome.trim() }, csrfToken,
            );
            setCodigo({ matricula: criado.matricula, nome: criado.nome, codigo: criado.codigo, sessoesEncerradas: 0 });
            setNovo((atual) => ({ ...atual, matricula: '', nome: '', lider: false }));
            await Promise.all([carregarTurma(turmaId), carregarTurmas()]);
        });
    }

    function alterar(jogador: Jogador, mudanca: AlteracaoParticipante) {
        if (!turmaId) return;
        void executar(async () => {
            await alterarParticipante(turmaId, jogador.id, mudanca, csrfToken);
            await carregarTurma(turmaId);
        });
    }

    function gerarCodigo(jogador: Jogador) {
        if (!turmaId) return;
        void executar(async () => {
            const trocado = await trocarCodigo(turmaId, jogador.id, csrfToken);
            setCodigo({
                matricula: trocado.matricula, nome: trocado.nome, codigo: trocado.codigo,
                sessoesEncerradas: trocado.sessoes_encerradas,
            });
            await carregarTurma(turmaId);
        });
    }

    if (turmas === null) return <p className="estado-variaveis">Carregando turmas…</p>;

    return (
        <div className="cadastro">
            {erro && <p className="mensagem-login aviso-cadastro" role="alert">{erro}</p>}

            <section className="bloco-cadastro" aria-labelledby="cadastro-turma">
                <h3 id="cadastro-turma">Turma</h3>
                <div className="linha-cadastro">
                    {turmas.length > 0 ? (
                        <label className="campo-turma">
                            Turma em edição
                            <select value={turmaId ?? ''} onChange={(e) => setTurmaId(e.target.value)}>
                                {turmas.map((turma) => (
                                    <option key={turma.id} value={turma.id}>
                                        {turma.id}{turma.nome ? ` · ${turma.nome}` : ''} · {turma.participantes} participantes
                                    </option>
                                ))}
                            </select>
                        </label>
                    ) : (
                        <p className="estado-variaveis">Nenhuma turma cadastrada. Crie a primeira ao lado.</p>
                    )}
                    <form className="form-cadastro" aria-label="Nova turma" onSubmit={aoCriarTurma}>
                        <label>
                            Id da turma
                            <input
                                value={novaTurma.id} maxLength={40} required placeholder="T02"
                                onChange={(e) => setNovaTurma({ ...novaTurma, id: e.target.value })}
                            />
                        </label>
                        <label>
                            Nome da turma
                            <input
                                value={novaTurma.nome} maxLength={120} required placeholder="Concessionárias Sul"
                                onChange={(e) => setNovaTurma({ ...novaTurma, nome: e.target.value })}
                            />
                        </label>
                        <button className="botao-entrar" type="submit" disabled={ocupado}>Criar turma</button>
                    </form>
                </div>
            </section>

            {turmaId && (
                <>
                    <section className="bloco-cadastro" aria-labelledby="cadastro-grupos">
                        <h3 id="cadastro-grupos">Grupos da turma {turmaId}</h3>
                        {grupos.length === 0 ? (
                            <p className="estado-variaveis">Esta turma ainda não tem grupos. O participante precisa de um.</p>
                        ) : (
                            <ul className="lista-grupos" aria-label="Grupos">
                                {grupos.map((grupo) => (
                                    <li key={grupo.id}>
                                        <code>{grupo.id}</code> {grupo.nome ?? 'sem nome'} · {grupo.participantes} participantes
                                    </li>
                                ))}
                            </ul>
                        )}
                        <form className="form-cadastro" aria-label="Novo grupo" onSubmit={aoCriarGrupo}>
                            <label>
                                Id do grupo
                                <input
                                    value={novoGrupo.id} maxLength={40} required placeholder="G01"
                                    onChange={(e) => setNovoGrupo({ ...novoGrupo, id: e.target.value })}
                                />
                            </label>
                            <label>
                                Nome do grupo
                                <input
                                    value={novoGrupo.nome} maxLength={120} required placeholder="Equipe Pulse"
                                    onChange={(e) => setNovoGrupo({ ...novoGrupo, nome: e.target.value })}
                                />
                            </label>
                            <button className="botao-entrar" type="submit" disabled={ocupado}>Criar grupo</button>
                        </form>
                    </section>

                    <section className="bloco-cadastro" aria-labelledby="cadastro-participantes">
                        <h3 id="cadastro-participantes">Participantes</h3>
                        <form className="form-cadastro" aria-label="Novo participante" onSubmit={aoCadastrar}>
                            <label>
                                Matrícula
                                <input
                                    value={novo.matricula} maxLength={40} required placeholder="A123456"
                                    onChange={(e) => setNovo({ ...novo, matricula: e.target.value })}
                                />
                            </label>
                            <label>
                                Nome
                                <input
                                    value={novo.nome} maxLength={120} required placeholder="Ana Lima"
                                    onChange={(e) => setNovo({ ...novo, nome: e.target.value })}
                                />
                            </label>
                            <label>
                                Grupo
                                <select
                                    value={novo.grupo_id} required
                                    onChange={(e) => setNovo({ ...novo, grupo_id: e.target.value })}
                                >
                                    {grupos.map((grupo) => (
                                        <option key={grupo.id} value={grupo.id}>{grupo.id}{grupo.nome ? ` · ${grupo.nome}` : ''}</option>
                                    ))}
                                </select>
                            </label>
                            <label className="campo-marcar">
                                <input
                                    type="checkbox" checked={novo.lider}
                                    onChange={(e) => setNovo({ ...novo, lider: e.target.checked })}
                                />
                                Líder do grupo
                            </label>
                            <button className="botao-entrar" type="submit" disabled={ocupado || grupos.length === 0}>
                                Cadastrar participante
                            </button>
                        </form>

                        {codigo && (
                            <div className="codigo-emitido" role="status">
                                <p>
                                    Código de acesso de <strong>{codigo.matricula}</strong>
                                    {codigo.nome ? ` (${codigo.nome})` : ''}: <code>{codigo.codigo}</code>
                                </p>
                                <p>
                                    Anote agora: ele não será mostrado de novo.
                                    {codigo.sessoesEncerradas > 0 && ` ${codigo.sessoesEncerradas} sessão(ões) encerrada(s).`}
                                </p>
                                <button className="botao-texto" type="button" onClick={() => setCodigo(null)}>Já anotei</button>
                            </div>
                        )}

                        {jogadores.length === 0 ? (
                            <p className="estado-variaveis">Esta turma ainda não tem participantes.</p>
                        ) : (
                            <table className="tabela-jogadores">
                                <thead>
                                    <tr>
                                        <th scope="col">Grupo</th>
                                        <th scope="col">Matrícula</th>
                                        <th scope="col">Nome</th>
                                        <th scope="col">Papel no grupo</th>
                                        <th scope="col">Situação</th>
                                        <th scope="col">Ações</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {jogadores.map((jogador) => (
                                        <tr key={jogador.id}>
                                            <td>{jogador.grupo_id ?? '—'}</td>
                                            <td>{jogador.login}</td>
                                            <td>{jogador.nome ?? '—'}</td>
                                            <td>{jogador.lider ? <span className="selo-lider">Líder</span> : 'Participante'}</td>
                                            <td>
                                                <span className={`selo-situacao selo-${jogador.situacao}`}>{SITUACAO[jogador.situacao]}</span>
                                            </td>
                                            <td className="acoes-cadastro">
                                                <button
                                                    className="botao-sair" type="button" disabled={ocupado}
                                                    aria-label={`Novo código para ${jogador.login}`}
                                                    onClick={() => gerarCodigo(jogador)}
                                                >
                                                    Novo código
                                                </button>
                                                {!jogador.lider && jogador.situacao !== 'inativo' && (
                                                    <button
                                                        className="botao-sair" type="button" disabled={ocupado}
                                                        aria-label={`Tornar ${jogador.login} líder`}
                                                        onClick={() => alterar(jogador, { lider: true })}
                                                    >
                                                        Tornar líder
                                                    </button>
                                                )}
                                                <button
                                                    className="botao-sair" type="button" disabled={ocupado}
                                                    aria-label={`${jogador.situacao === 'inativo' ? 'Reativar' : 'Desativar'} ${jogador.login}`}
                                                    onClick={() => alterar(jogador, { ativo: jogador.situacao === 'inativo' })}
                                                >
                                                    {jogador.situacao === 'inativo' ? 'Reativar' : 'Desativar'}
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </section>
                </>
            )}
        </div>
    );
}
